import { apiSlice } from '../apiSlice';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	StampFilter,
	GamePhilatelySeriesDTO,
	GamePhilatelySeriesDTORequest,
	GamePhilatelyTreasureDTO,
	PublishDTO,
	TreasureFilter,
} from '../DTO/treasury/stamp.interface';

export const stampApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getStampPage: builder.query<
			SuccessResponse<Paginated<GamePhilatelySeriesDTO>>,
			StampFilter
		>({
			query: (body) => ({
				url: `/web/game/philately/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['StampList'],
		}),
		getStampSortList: builder.query<
			SuccessResponse<GamePhilatelySeriesDTO[]>,
			null
		>({
			query: () => ({
				url: `/web/game/philately/series/sort`,
				method: 'GET',
			}),
		}),
		sortStamp: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/game/philately/series/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['StampList'],
		}),
		createStamp: builder.mutation<
			SuccessResponse<any>,
			GamePhilatelySeriesDTORequest
		>({
			query: (body) => ({
				url: `/web/game/philately`,
				method: 'POST',
				body,
			}),
		}),
		updateStamp: builder.mutation<
			SuccessResponse<any>,
			GamePhilatelySeriesDTORequest
		>({
			query: (body) => ({
				url: `/web/game/philately/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['StampDetail'],
		}),
		setStampStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: PublishDTO[] }
		>({
			query: (body) => ({
				url: `/web/game/philately/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['StampList', 'StampDetail'],
		}),
		getStampDetail: builder.query<
			SuccessResponse<GamePhilatelySeriesDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/game/philately/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['StampDetail'],
		}),
		getStampTreasurePage: builder.query<
			SuccessResponse<Paginated<GamePhilatelyTreasureDTO>>,
			TreasureFilter
		>({
			query: (body) => ({
				url: `/web/game/philately/series/${body.seriesId}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['StampTreasureList'],
		}),
		getStampTreasureSortList: builder.query<
			SuccessResponse<GamePhilatelyTreasureDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/game/philately/series/${id}/sort`,
				method: 'GET',
			}),
		}),
		sortStampTreasure: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/game/philately/treasure/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['StampTreasureList'],
		}),
		setStampTreasureStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: PublishDTO[]; id: string }
		>({
			query: (body) => ({
				url: `/web/game/philately/treasure/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['StampTreasureList', 'StampTreasureDetail'],
		}),
		deleteStamp: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/game/philately/series/${id}`,
				method: 'DELETE',
			}),
		}),
		getStampTreasureDetail: builder.query<
			SuccessResponse<GamePhilatelyTreasureDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/game/philately/treasure/${id}`,
				method: 'GET',
			}),
			providesTags: ['StampTreasureDetail'],
		}),
		updateStampTreasureDetail: builder.mutation<
			SuccessResponse<any>,
			GamePhilatelyTreasureDTO
		>({
			query: (body) => ({
				url: `/web/game/philately/treasure/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['StampTreasureDetail'],
		}),
		createStampTreasure: builder.mutation<
			SuccessResponse<any>,
			GamePhilatelyTreasureDTO
		>({
			query: (body) => ({
				url: `/web/game/philately/series/${body.stampId}`,
				method: 'POST',
				body,
			}),
		}),
		deleteStampTreasure: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/game/philately/treasure/${id}`,
				method: 'DELETE',
			}),
		}),
	}),
});

export const {
	useGetStampPageQuery,
	useGetStampSortListQuery,
	useSortStampMutation,
	useCreateStampMutation,
	useUpdateStampMutation,
	useSetStampStatusMutation,
	useGetStampDetailQuery,
	useGetStampTreasurePageQuery,
	useGetStampTreasureSortListQuery,
	useSortStampTreasureMutation,
	useSetStampTreasureStatusMutation,
	useDeleteStampMutation,
	useGetStampTreasureDetailQuery,
	useUpdateStampTreasureDetailMutation,
	useCreateStampTreasureMutation,
	useDeleteStampTreasureMutation,
} = stampApiSlice;
